import { useState, useEffect } from "react";
import { saveMarketplaceOrder } from "../lib/dataStore";
import { toast } from "sonner";
import { X, Store, Calculator, Check, Building2, Calendar, FileText } from "lucide-react";

const MARKETPLACES = [
  "Amazon",
  "Flipkart",
  "Meesho",
  "Myntra",
  "JioMart",
  "Tata CLIQ",
  "Cred",
  "Nykaa",
  "Other",
];

const STATUSES = ["Settled", "Pending Settlement", "On Hold"];

export default function MarketplaceOrderModal({ initialOrder, products = [], onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    id: initialOrder?.id || null,
    marketplace: initialOrder?.marketplace || "Amazon",
    marketplaceCustomName: initialOrder?.marketplaceCustomName || "",
    settlementRef: initialOrder?.settlementRef || "",
    startDate: initialOrder?.startDate || new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10),
    endDate: initialOrder?.endDate || new Date().toISOString().slice(0, 10),
    payoutDate: initialOrder?.payoutDate || new Date().toISOString().slice(0, 10),
    status: initialOrder?.status || "Settled",
    unitsSold: initialOrder?.unitsSold ?? "",
    itemsSummary: initialOrder?.itemsSummary || "",
    grossSales: initialOrder?.grossSales ?? "",
    commissionFee: initialOrder?.commissionFee ?? "",
    logisticsFee: initialOrder?.logisticsFee ?? "",
    taxDeductions: initialOrder?.taxDeductions ?? "",
    otherDeductions: initialOrder?.otherDeductions ?? "",
    netPayout: initialOrder?.netPayout ?? "",
    cogs: initialOrder?.cogs ?? "",
    netProfit: initialOrder?.netProfit ?? "",
    utrNumber: initialOrder?.utrNumber || "",
    paymentAccount: initialOrder?.paymentAccount || "HDFC Bank",
    notes: initialOrder?.notes || "",
  }));

  const [isManualNetPayout, setIsManualNetPayout] = useState(false);
  const [isManualNetProfit, setIsManualNetProfit] = useState(false);
  const [busy, setBusy] = useState(false);

  // Auto-calculated Net Payout
  const calculatedNetPayout = Math.max(
    0,
    Number(form.grossSales || 0) -
      Number(form.commissionFee || 0) -
      Number(form.logisticsFee || 0) -
      Number(form.taxDeductions || 0) -
      Number(form.otherDeductions || 0)
  );

  // Auto-calculated Net Profit
  const effectiveNetPayout = isManualNetPayout ? Number(form.netPayout || 0) : calculatedNetPayout;
  const calculatedNetProfit = effectiveNetPayout - Number(form.cogs || 0);

  useEffect(() => {
    if (!isManualNetPayout) {
      setForm((f) => ({ ...f, netPayout: calculatedNetPayout }));
    }
  }, [form.grossSales, form.commissionFee, form.logisticsFee, form.taxDeductions, form.otherDeductions, isManualNetPayout, calculatedNetPayout]);

  useEffect(() => {
    if (!isManualNetProfit) {
      setForm((f) => ({ ...f, netProfit: calculatedNetProfit }));
    }
  }, [form.netPayout, form.cogs, effectiveNetPayout, isManualNetProfit, calculatedNetProfit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.settlementRef.trim() && !form.itemsSummary.trim()) {
      return toast.error("Please enter a Settlement Reference ID or Items Summary");
    }
    if (!form.grossSales || Number(form.grossSales) <= 0) {
      return toast.error("Please enter a valid Gross Sales Amount");
    }

    setBusy(true);
    try {
      await saveMarketplaceOrder({
        ...form,
        unitsSold: Number(form.unitsSold || 0),
        grossSales: Number(form.grossSales || 0),
        commissionFee: Number(form.commissionFee || 0),
        logisticsFee: Number(form.logisticsFee || 0),
        taxDeductions: Number(form.taxDeductions || 0),
        otherDeductions: Number(form.otherDeductions || 0),
        netPayout: Number(form.netPayout || 0),
        cogs: Number(form.cogs || 0),
        netProfit: Number(form.netProfit || 0),
      });

      toast.success(form.id ? "Marketplace Order details updated!" : "Marketplace Order / Settlement logged!");
      onSaved();
    } catch (err) {
      toast.error(err.message || "Failed to save marketplace order");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-foreground/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-background w-full max-w-3xl max-h-[92vh] overflow-y-auto border border-border shadow-2xl rounded-2xl my-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-background z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <div className="overline text-terracotta text-xs">Marketplace Payout Entry</div>
              <h2 className="font-serif text-2xl font-bold">
                {form.id ? "Edit Marketplace Order Details" : "Add Marketplace Order / Settlement"}
              </h2>
            </div>
          </div>
          <button
            type="button"
            data-testid="mp-modal-close"
            onClick={onClose}
            className="p-2 text-muted-foreground hover:text-foreground rounded-full hover:bg-muted"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Marketplace & Settlement Ref */}
          <div className="grid sm:grid-cols-3 gap-4">
            <label className="block">
              <span className="block text-xs font-semibold mb-1 text-muted-foreground">Marketplace *</span>
              <select
                data-testid="mp-select"
                value={form.marketplace}
                onChange={(e) => setForm({ ...form, marketplace: e.target.value })}
                className="w-full bg-muted/50 border border-border px-3 py-2.5 rounded-lg text-sm font-medium outline-none focus:border-terracotta"
              >
                {MARKETPLACES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </label>

            {form.marketplace === "Other" ? (
              <label className="block">
                <span className="block text-xs font-semibold mb-1 text-muted-foreground">Custom Platform Name *</span>
                <input
                  data-testid="mp-custom-name"
                  type="text"
                  required
                  placeholder="e.g. LimeRoad, Ajio, Custom Store"
                  value={form.marketplaceCustomName}
                  onChange={(e) => setForm({ ...form, marketplaceCustomName: e.target.value })}
                  className="w-full bg-muted/50 border border-border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-terracotta"
                />
              </label>
            ) : (
              <label className="block">
                <span className="block text-xs font-semibold mb-1 text-muted-foreground">Settlement / Payout Ref ID *</span>
                <input
                  data-testid="mp-settlement-ref"
                  type="text"
                  placeholder="e.g. AMZ-PAY-2026-W32"
                  value={form.settlementRef}
                  onChange={(e) => setForm({ ...form, settlementRef: e.target.value })}
                  className="w-full bg-muted/50 border border-border px-3 py-2.5 rounded-lg text-sm font-mono outline-none focus:border-terracotta"
                />
              </label>
            )}

            <label className="block">
              <span className="block text-xs font-semibold mb-1 text-muted-foreground">Settlement Status</span>
              <select
                data-testid="mp-status"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full bg-muted/50 border border-border px-3 py-2.5 rounded-lg text-sm font-medium outline-none focus:border-terracotta"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
          </div>

          {/* Date Range & Payout Date */}
          <div className="bg-muted/30 border border-border/80 p-4 rounded-xl space-y-3">
            <div className="flex items-center gap-2 text-xs font-bold text-foreground uppercase tracking-wider">
              <Calendar className="w-4 h-4 text-terracotta" /> Settlement Period / Date Range &amp; Bank Payout Date
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <label className="block">
                <span className="block text-xs mb-1 text-muted-foreground">Period Start Date *</span>
                <input
                  data-testid="mp-start-date"
                  type="date"
                  required
                  value={form.startDate}
                  onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                  className="w-full bg-background border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-terracotta"
                />
              </label>

              <label className="block">
                <span className="block text-xs mb-1 text-muted-foreground">Period End Date *</span>
                <input
                  data-testid="mp-end-date"
                  type="date"
                  required
                  value={form.endDate}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                  className="w-full bg-background border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-terracotta"
                />
              </label>

              <label className="block">
                <span className="block text-xs mb-1 text-muted-foreground">Bank Payout Date *</span>
                <input
                  data-testid="mp-payout-date"
                  type="date"
                  required
                  value={form.payoutDate}
                  onChange={(e) => setForm({ ...form, payoutDate: e.target.value })}
                  className="w-full bg-background border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-terracotta"
                />
              </label>
            </div>
          </div>

          {/* Units Sold & Items Summary */}
          <div className="grid sm:grid-cols-4 gap-4">
            <label className="block sm:col-span-1">
              <span className="block text-xs font-semibold mb-1 text-muted-foreground">Total Units Sold (Pcs)</span>
              <input
                data-testid="mp-units-sold"
                type="number"
                min="0"
                placeholder="e.g. 25"
                value={form.unitsSold}
                onChange={(e) => setForm({ ...form, unitsSold: e.target.value })}
                className="w-full bg-muted/50 border border-border px-3 py-2.5 rounded-lg text-sm font-price font-bold outline-none focus:border-terracotta"
              />
            </label>

            <label className="block sm:col-span-3">
              <span className="block text-xs font-semibold mb-1 text-muted-foreground">Products / Order Items Summary</span>
              <input
                data-testid="mp-items-summary"
                type="text"
                placeholder="e.g. 10x Leather Totes, 8x Cotton Bags, 7x Wallets"
                value={form.itemsSummary}
                onChange={(e) => setForm({ ...form, itemsSummary: e.target.value })}
                className="w-full bg-muted/50 border border-border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-terracotta"
              />
            </label>
          </div>

          {/* Financial Breakdown Section */}
          <div className="border border-border p-4 rounded-xl bg-card space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-2">
              <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                <Calculator className="w-4 h-4 text-terracotta" /> Payout &amp; Deductions Breakdown
              </div>
              <span className="text-[11px] text-muted-foreground">
                Net Payout = Gross Sales − Fees − Shipping − Taxes − Deductions
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <label className="block">
                <span className="block text-xs font-semibold mb-1 text-muted-foreground">Gross Sales (₹) *</span>
                <input
                  data-testid="mp-gross-sales"
                  type="number"
                  min="0"
                  required
                  placeholder="e.g. 25000"
                  value={form.grossSales}
                  onChange={(e) => setForm({ ...form, grossSales: e.target.value })}
                  className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm font-price font-bold text-foreground outline-none focus:border-terracotta"
                />
              </label>

              <label className="block">
                <span className="block text-xs mb-1 text-muted-foreground">Commission Fee (₹)</span>
                <input
                  data-testid="mp-commission-fee"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={form.commissionFee}
                  onChange={(e) => setForm({ ...form, commissionFee: e.target.value })}
                  className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm font-price outline-none focus:border-terracotta"
                />
              </label>

              <label className="block">
                <span className="block text-xs mb-1 text-muted-foreground">Logistics / Courier (₹)</span>
                <input
                  data-testid="mp-logistics-fee"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={form.logisticsFee}
                  onChange={(e) => setForm({ ...form, logisticsFee: e.target.value })}
                  className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm font-price outline-none focus:border-terracotta"
                />
              </label>

              <label className="block">
                <span className="block text-xs mb-1 text-muted-foreground">TCS / TDS / Tax (₹)</span>
                <input
                  data-testid="mp-tax-deductions"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={form.taxDeductions}
                  onChange={(e) => setForm({ ...form, taxDeductions: e.target.value })}
                  className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm font-price outline-none focus:border-terracotta"
                />
              </label>

              <label className="block">
                <span className="block text-xs mb-1 text-muted-foreground">Ads / Other Deductions (₹)</span>
                <input
                  data-testid="mp-other-deductions"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={form.otherDeductions}
                  onChange={(e) => setForm({ ...form, otherDeductions: e.target.value })}
                  className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm font-price outline-none focus:border-terracotta"
                />
              </label>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 pt-3 border-t border-border/80">
              {/* Net Payout Received */}
              <div className="bg-emerald-500/10 border border-emerald-500/20 p-3 rounded-xl">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-emerald-800 dark:text-emerald-300">
                    Net Payout Settled (₹)
                  </span>
                  <button
                    type="button"
                    onClick={() => setIsManualNetPayout(!isManualNetPayout)}
                    className="text-[10px] text-emerald-700 underline"
                  >
                    {isManualNetPayout ? "Reset Auto" : "Override"}
                  </button>
                </div>
                <input
                  data-testid="mp-net-payout"
                  type="number"
                  value={form.netPayout}
                  onChange={(e) => {
                    setIsManualNetPayout(true);
                    setForm({ ...form, netPayout: e.target.value });
                  }}
                  className="w-full bg-background border border-emerald-300 font-price font-extrabold text-xl px-3 py-1.5 rounded-md text-emerald-700 dark:text-emerald-400 outline-none"
                />
              </div>

              {/* COGS / Purchase Cost */}
              <div className="bg-amber-500/10 border border-amber-500/20 p-3 rounded-xl">
                <span className="text-xs font-bold uppercase tracking-wider text-amber-800 dark:text-amber-300 block mb-1">
                  Product COGS / Material Cost (₹)
                </span>
                <input
                  data-testid="mp-cogs"
                  type="number"
                  min="0"
                  placeholder="e.g. 11000"
                  value={form.cogs}
                  onChange={(e) => setForm({ ...form, cogs: e.target.value })}
                  className="w-full bg-background border border-amber-300 font-price font-bold text-xl px-3 py-1.5 rounded-md text-amber-800 dark:text-amber-300 outline-none"
                />
              </div>

              {/* Net Profit */}
              <div className="bg-terracotta/10 border border-terracotta/20 p-3 rounded-xl">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-terracotta">
                    Calculated Net Profit (₹)
                  </span>
                  <button
                    type="button"
                    onClick={() => setIsManualNetProfit(!isManualNetProfit)}
                    className="text-[10px] text-terracotta underline"
                  >
                    {isManualNetProfit ? "Reset Auto" : "Override"}
                  </button>
                </div>
                <input
                  data-testid="mp-net-profit"
                  type="number"
                  value={form.netProfit}
                  onChange={(e) => {
                    setIsManualNetProfit(true);
                    setForm({ ...form, netProfit: e.target.value });
                  }}
                  className="w-full bg-background border border-terracotta font-price font-extrabold text-xl px-3 py-1.5 rounded-md text-terracotta outline-none"
                />
              </div>
            </div>
          </div>

          {/* Bank & Payment Reference */}
          <div className="grid sm:grid-cols-3 gap-4">
            <label className="block">
              <span className="block text-xs font-semibold mb-1 text-muted-foreground">Deposit Bank Account</span>
              <input
                data-testid="mp-payment-account"
                type="text"
                placeholder="e.g. HDFC Bank, ICICI Bank"
                value={form.paymentAccount}
                onChange={(e) => setForm({ ...form, paymentAccount: e.target.value })}
                className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-terracotta"
              />
            </label>

            <label className="block">
              <span className="block text-xs font-semibold mb-1 text-muted-foreground">Bank UTR / Transaction No.</span>
              <input
                data-testid="mp-utr-number"
                type="text"
                placeholder="e.g. N230491820938"
                value={form.utrNumber}
                onChange={(e) => setForm({ ...form, utrNumber: e.target.value })}
                className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm font-mono outline-none focus:border-terracotta"
              />
            </label>

            <label className="block">
              <span className="block text-xs font-semibold mb-1 text-muted-foreground">Remarks / Notes</span>
              <input
                data-testid="mp-notes"
                type="text"
                placeholder="e.g. Weekly settlement after return deductions"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-terracotta"
              />
            </label>
          </div>

          {/* Form Actions */}
          <div className="border-t border-border pt-4 flex items-center justify-between gap-4">
            <div className="text-xs text-muted-foreground font-price font-medium">
              Gross: ₹{form.grossSales || 0} → Net Payout: ₹{form.netPayout || 0} → Profit: ₹{form.netProfit || 0}
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 rounded-full border border-border text-sm hover:bg-muted font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="mp-save-btn"
                disabled={busy}
                className="px-6 py-2.5 rounded-full bg-terracotta text-primary-foreground font-medium text-sm hover:opacity-90 disabled:opacity-40 shadow-sm transition"
              >
                {busy ? "Saving Settlement…" : form.id ? "Update Settlement" : "Save Marketplace Settlement"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
